import React, { useState, useEffect } from 'react';

import { Box, Button, IconButton, Menu, MenuItem, Dropdown, MenuButton, Tooltip, Typography, Avatar } from '@mui/joy';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import ModalOptimize from './ModalOptimize';
import MediaUpload from './MediaUpload';
import ToolbarTop from './ToolbarTop';
import PostToolbar from './Toolbar';
import MediaDisplay from './MediaDisplay';
import EmojiPicker from './EmojiPicker';
import ModalClose from '@mui/joy/ModalClose';

/* Custom Hooks */
import useDeleteFile from '@/utils/useDeleteFile';
import useOptimizePost from '@/utils/useOptimizePost';
import useUploadFile from '@/utils/useUploadFile';
import useBell from '@/utils/useBell';

import { boldness } from '@/utils/rich-text/boldness';
import { italicize } from '@/utils/rich-text/italic';
import { toggleBulletPoints } from '@/utils/rich-text/bulletpoints';
import { toggleNumberedList } from '@/utils/rich-text/numberedlist';

// import boldness and italic from mui material icons and underline
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';

import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CheckIcon from '@mui/icons-material/Check';
import Divider from '@mui/joy/Divider';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSelectedUserAccount } from '@/contexts/SelectedUserAccountContext';
import { toast } from 'react-toastify';



const EditPost = ({ postId, session, content, setContent, postMedia, setPostMedia, setCurrentMediaType, postStrength, postStrengthLoading, handlePostStrength, showPostStrength, setShowPostStrength }) => {
  const [openMediaModal, setOpenMediaModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [extractedUrls, setExtractedUrls] = useState('');
  const [selectedText, setSelectedText] = useState({ start: 0, end: 0, selectedText: '' });
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const { selectedUser, selectUserAccount } = useSelectedUserAccount();

  const [history, setHistory] = useState([]);
  const [isOptimizeModalOpen, setIsOptimizeModalOpen] = useState(false);
  const { isLoadingOptimization, optimizationMessages, setOptimizationMessages, optimizePost } = useOptimizePost();
  const { playBellSound } = useBell();
  const { file, setFile, uploadFile } = useUploadFile();
  const { deleteFile, isDeleting, error } = useDeleteFile();

  const handleOptimize = async (type, text) => {
    setIsOptimizeModalOpen(true);
    optimizePost(type, text, playBellSound);
  }

  const handleCloseModalOptimize = () => {
    setIsOptimizeModalOpen(false);
    setOptimizationMessages([]);
    setHistory([]);
  }

  const fetchOrganizationMembers = async () => {
    try {
      const res = await fetch(`/api/organization/members`)
      const data = await res.json();

      if (data.success) {
        setOrganizationMembers(data.members);
      }
    } catch (error) {
      console.error('Error fetching organization members:', error);
    }
  };

  const extractUrls = (text) => {
    const safeText = text || '';   // ✅ guarantees it's always a string
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return safeText.match(urlRegex) || []; // ✅ match returns null if no match
  };


  const handleDeleteMedia = async (fileType, photo) => {
    const response = await deleteFile(photo);

    if (response.success) {
      if (fileType === 'photo') {
        const newMedia = postMedia.photos.filter((item) => item !== photo);
        setPostMedia({ ...postMedia, photos: newMedia });
      }

      if (fileType === 'pdf') {
        setPostMedia({ ...postMedia, pdf: '' });
      }

      if (fileType === 'video') {
        setPostMedia({ ...postMedia, video: '' });
      }
    }
  }

  const handleGeneralContentChange = (newContent) => {
    const urls = extractUrls(newContent);

    if (urls.length > 0) {
      // Assume the last URL is the main link in the content
      const mainLink = urls[urls.length - 1];

      if (mainLink !== extractedUrls) {
        setExtractedUrls(mainLink);

        // Make an API request to fetch image preview
        const fetchMetadata = async () => {
          try {
            const res = await fetch(`/api/fetch-metadata?url=${encodeURIComponent(mainLink)}`);
            const data = await res.json();
            if (data?.valid) {
              const uploadLink = await handleUploadFileLink(data?.image, mainLink);
            }
          } catch (error) {
            console.error('Error fetching metadata:', error);
          }
        }

        fetchMetadata();
      }
    }

    setContent(newContent || '');
    setCursorPosition(newContent?.length || 0);
  };

  const handleUploadFileLink = async (targetLink, link) => {
    try {
      const link_source = link || null;
      const result = await uploadFile(postId, null, targetLink, link_source);

      if (result.success) {
        setPostMedia(prevState => ({
          ...prevState,
          photos: [...prevState.photos, result.url]
        }));
      }

    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const textAreaPosition = (e) => {
    const start = e.target.selectionStart;
    const end = e.target.selectionEnd;
    const selectedText = e.target.value.substring(start, end);

    setCursorPosition(end);  // Assuming this updates state for cursor position
    setSelectedText({ start, end, selectedText });
  };

  // Function to insert emoji at the current cursor position
  const handleEmojiSelect = (emoji) => {
    const safeContent = content || ''; // ✅ protect against null
    const start = safeContent.substring(0, cursorPosition);
    const end = safeContent.substring(cursorPosition);

    setContent(start + emoji.native + end);
    setShowEmojiPicker(false);
  };

  const toggleBold = () => {
    const safeContent = content || ''; // ✅ always a string
    const { start, end, selectedText: currentSelectedText } = selectedText;

    if (currentSelectedText !== '') {
      const newText = boldness(currentSelectedText);
      setContent(
        safeContent.substring(0, start) + newText + safeContent.substring(end)
      );
      setSelectedText({ start, end: start + newText.length, selectedText: newText });
    }
  };

  const toggleItalic = () => {
    const safeContent = content || '';
    const { start, end, selectedText: currentSelectedText } = selectedText;

    if (currentSelectedText !== '') {
      const newText = italicize(currentSelectedText);
      setContent(
        safeContent.substring(0, start) + newText + safeContent.substring(end)
      );
      setSelectedText({ start, end: start + newText.length, selectedText: newText });
    }
  };

  const toggleBullets = () => {
    const safeContent = content || '';
    const { start, end, selectedText: currentSelectedText } = selectedText;

    if (currentSelectedText && currentSelectedText.trim() !== '') {
      const newText = toggleBulletPoints(currentSelectedText);
      setContent(
        safeContent.substring(0, start) + newText + safeContent.substring(end)
      );
      setSelectedText({ start, end: start + newText.length, selectedText: newText });
    } else {
      toast.error('Please select some text to apply the formatting', {
        position: 'top-right'
      });
    }
  };

  const toggleNumbered = () => {
    const safeContent = content || '';
    const { start, end, selectedText: currentSelectedText } = selectedText;

    if (currentSelectedText && currentSelectedText.trim() !== '') {
      const newText = toggleNumberedList(currentSelectedText);
      setContent(
        safeContent.substring(0, start) + newText + safeContent.substring(end)
      );
      setSelectedText({ start, end: start + newText.length, selectedText: newText });
    } else {
      toast.error('Please select some text to apply numbered list', {
        position: 'top-right'
      });
    }
  };



  useEffect(() => {
    fetchOrganizationMembers();
  }, []);


  return (
    <>
      <ModalOptimize
        isOptimizeModalOpen={isOptimizeModalOpen}
        optimizeCloseModal={handleCloseModalOptimize}
        session={session}
        history={history}
        setHistory={setHistory}
        messages={optimizationMessages}
        setMessages={setOptimizationMessages}
        optimizeLoading={isLoadingOptimization}
        handleOptimize={handleOptimize}
        content={content}
        setContent={setContent}
        setIsOptimizeModalOpen={setIsOptimizeModalOpen}
        setOptimizeLoading={setIsOptimizeModalOpen}
      />

      {session && (
        <MediaUpload
          postId={postId}
          openMediaModal={openMediaModal}
          setOpenMediaModal={setOpenMediaModal}
          postMedia={postMedia}
          setPostMedia={setPostMedia}
          setCurrentMediaType={setCurrentMediaType}
          session={session}
        />
      )}

      <Box sx={{ padding: '0rem' }}>
        <div style={{ position: 'relative' }}>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            {session?.settings?.user_status === 'active' && (


              <Dropdown>
                <MenuButton endDecorator={<ArrowDropDownIcon />} sx={{ border: 'none', borderRight: '1px solid #e0e0e0' }}>
                  <Avatar src={selectedUser?.profile_image || ''} sx={{ width: 40, height: 40, ml: 2, mr: 1 }} />
                </MenuButton>
                <Menu placement="bottom-end">
                  <Menu sx={{ zIndex: 10000000000, maxHeight: '150px' }}>
                    {organizationMembers.map((member) => (
                      <React.Fragment key={member.user_id}>
                        <MenuItem onClick={() => selectUserAccount(member)} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar src={member.profile_image} sx={{ width: 40, height: 40, mr: 2 }} />
                          <Typography sx={{ flexGrow: 1 }}>{member.name}</Typography>
                          {selectedUser.user_id === member.user_id && <CheckIcon />}
                        </MenuItem>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </Menu>
                </Menu>
              </Dropdown>
            )}



            <Typography level="title-lg" sx={{ p: '12px 20px' }}>
              Post Editor
            </Typography>
          </Box>



          <ModalClose
            variant="plain"
            sx={{
              m: 0,
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1100 // Higher than the tabs
            }}
          />

          <Divider sx={{ mb: 1 }} />

          <ToolbarTop
            toggleBold={toggleBold}
            toggleItalic={toggleItalic}
            toggleBullets={toggleBullets}
            toggleNumbered={toggleNumbered}
            content={content}
            showEmojiPicker={showEmojiPicker}
            setShowEmojiPicker={setShowEmojiPicker}
            setOpenMediaModal={setOpenMediaModal}
            handleOptimize={handleOptimize}
            postStrength={postStrength}
            postStrengthLoading={postStrengthLoading}
            handlePostStrength={handlePostStrength}
            showPostStrength={showPostStrength}
            setShowPostStrength={setShowPostStrength}
          />

          <TextField
            value={content || ''}
            placeholder="Write your post here..."
            sx={{ p: 1 }}
            onSelect={textAreaPosition}
            onChange={(e) => handleGeneralContentChange(e.target.value)}
            multiline
            rows={17}
            fullWidth
          />

          <EmojiPicker
            showEmojiPicker={showEmojiPicker}
            setShowEmojiPicker={setShowEmojiPicker}
            onEmojiSelect={handleEmojiSelect}
          />
        </div>




        <PostToolbar
          content={content}
          showEmojiPicker={showEmojiPicker}
          setShowEmojiPicker={setShowEmojiPicker}
          setOpenMediaModal={setOpenMediaModal}
          handleOptimize={handleOptimize}
          postStrengthLoading={postStrengthLoading}
          handlePostStrength={handlePostStrength}
        />


        <MediaDisplay postMedia={postMedia} handleDeleteMedia={handleDeleteMedia} />
      </Box>
    </>
  );
}

export default EditPost;
